import { BookingEngine, Rates, roomData } from "../../index";
import Selectors from "./Selectors";
import waitForSelectors from "../../utilities/waitForSelectors";
import { handleIntegrationInjector } from "../../hotelIntegrationHandlers";
import axios from "axios";
import { guestbookSites, environment } from "../../../settings/IntegrationConstants";
import moment from "moment";
import {
  setNativeValue,
  getBrowserInfo,
  getCurrencySymbol,
  getDeviceInfo,
} from "../../utilities/utils";

declare global {
  interface Window {
    gb_data: any;
    data: any;
  }
}

const gbData = window.gb_data;

const Dedge: BookingEngine = {
  name: gbData.bookingEngine,
  hotel: {
    name: gbData.hotel.name,
    isAutomated: gbData.hotel.isAutomated,
    rateCodeExclusions: gbData.hotel.rateCodeExclusions,
    packageRateExlusions: gbData.hotel.packageRateExlusions,
    whiteLabel: gbData.hotel.whiteLabel,
    dontAbTrack: true,
    memberCode: gbData.hotel.memberCode,
    memberCodeType: gbData.hotel.memberCodeType,
    showEnrollment: JSON.parse(
      sessionStorage.getItem("gbShowEnrollment") || "true"
    ),
    widgetIdentifier: gbData.widget.identifier,
    primaryColor: gbData.widget.primaryColor,
    secondaryColor: gbData.widget.secondaryColor,
    widgetAnchored: gbData.widget.widgetAnchored,
    widgetWidth: gbData.widget.widgetWidth,
    widgetPositionTop: gbData.widget.widgetPositionTop,
    widgetPositionRight: gbData.widget.widgetPositionRight,
    widgetPositionBottom: gbData.widget.widgetPositionBottom,
    widgetPositionLeft: gbData.widget.widgetPositionLeft,
    inheritBeFont: gbData.widget.inheritBeFont,
    desktopEnrollmentPrimaryColor: gbData.widget.desktopEnrollmentPrimaryColor,
    benefits: gbData.widget.benefits,
  },
  selectors: Selectors,
  singlePageApp: false,
  widgetFixedPosition: !gbData.widget.widgetAnchored,
  largeEnrollmentMessage: true,
  activeIntervals: [],
  saveInfoAtConfirmation: true,
  // grab widget target
  getWidgetTarget: () => document.querySelector(Dedge.selectors.widget.target),
  // should user log in, grab the member code and input it (if member code exists)
  getMemberCode: () => {},
  clearMemberCode: () => {},
  // grab rates info and target to inject, it also applies rate exclusions if any
  getRates: () => {
    const ratesInfo: Rates[] = [];
    const rates = document.querySelectorAll(Dedge.selectors.ratesPage.rates);
    rates.forEach((rate: any, index: number) => {
      let shouldDisplay = true;

      const messageTarget = (rate as HTMLElement).parentElement;
      if (messageTarget) {
        const injectedElement = !!messageTarget.querySelector(
          Dedge.selectors.ratesPage.ratesMessage
        )
          ? messageTarget.querySelector(Dedge.selectors.ratesPage.ratesMessage)
          : document.createElement("div");
        if (
          !messageTarget.querySelector(
            Dedge.selectors.ratesPage.ratesMessage
          ) &&
          injectedElement
        ) {
          injectedElement.setAttribute(
            "class",
            Dedge.selectors.ratesPage.ratesMessage.substr(1)
          );
          messageTarget.append(injectedElement);
        }

        let ratePrice = 0;
        const taxedRate = window.gb_data.hotel.taxedRate;
        const lang = Dedge.getLanguage();

        if (lang !== "en") {
          ratePrice = rate.textContent
            .replace(",", ".")
            .replace(/[^\d.-]/g, "");
        } else {
          ratePrice = rate.textContent.replace(/[^\d.-]/g, "");
        }

        let calculatedRewards = (ratePrice / taxedRate) * 0.15;

        ratesInfo.push({
          rewardsAmount: (calculatedRewards as any).toFixed(0),
          shouldDisplay: shouldDisplay,
          target: injectedElement,
        });
      }
    });
    sessionStorage.setItem("rates", JSON.stringify(ratesInfo));
    return ratesInfo;
  },
  // grab confirmation number
  getConfirmationNumber: () => {
    let confimationNumbers = [] as string[];
    confimationNumbers.push(
      document.querySelectorAll(
        Dedge.selectors.confirmationPage.confirmationNumber
      )[0].textContent
    );
    return confimationNumbers;
  },
  // grab confirmation message target
  getConfirmationTarget: () =>
    document.querySelector(
      Dedge.selectors.confirmationPage.confirmationMessageContainer
    ),
  // grab displayed rates page currency
  getRatesCurrency: () => {
    let currency;
    let subtotal = document.querySelectorAll(Dedge.selectors.ratesPage.total);
    let ratesPageCurrency = document.querySelector(
      Dedge.selectors.ratesPage.currency
    );

    if (subtotal.length > 0) {
      currency = subtotal[0].textContent.replace(/[0-9,.]/g, "");
    } else if (ratesPageCurrency) {
      currency = ratesPageCurrency.value;
    } else {
      currency = "USD";
    }

    return getCurrencySymbol(currency);
  },
  // grab displayed checkout page currency
  getCheckoutCurrency: () => {
    if (window.location.href.indexOf("bookingReference") > -1) {
      return getCurrencySymbol(window.data.transaction.currency);
    }
    return Dedge.getRatesCurrency();
  },
  // grab currency code to save
  getCurrencyToSave: () => {
    return window.data.transaction.currency;
  },
  // grab language
  getLanguage: () => {
    let locale;

    if (window.location.href.indexOf("bookingReference") > -1) {
      locale = window.location.href
        .split("2DW3")[1]
        .split("/Confirmation")[0]
        .replace("/", "");
    } else {
      const targetElement = document.querySelector("html") as HTMLElement;
      const lang = targetElement.getAttribute("lang")?.split("-")[0];
      if (typeof lang === "string") {
        locale = lang;
      } else {
        locale = "en";
      }
    }

    return locale;
  },
  // grab enrollment target
  getEnrollmentTarget: () =>
    document.querySelector(
      Dedge.selectors.paymentPage.enrollmentMessageContainer
    ),
  // grab checkin date
  getPaymentPageCheckinDate: () => {
    return window.data.context.searchParameters.checkinDate;
  },
  // grab checkout date
  getPaymentPageCheckoutDate: () => {
    return window.data.context.searchParameters.checkoutDate;
  },
  // prefill user info if user logged in
  prefillUserInfo: (firstName = "", lastName = "", email = "") => {
    waitForSelectors(Dedge.selectors.paymentPage.waitFor, () => {
      const emailInput = document.querySelector(
        Dedge.selectors.paymentPage.email
      );
      const firstNameInput = document.querySelector(
        Dedge.selectors.paymentPage.firstNameInput
      );

      const lastNameInput = document.querySelector(
        Dedge.selectors.paymentPage.lastNameInput
      );
      const event = new Event("input", { bubbles: true });

      setNativeValue(firstNameInput, firstName);
      firstNameInput.focus();
      firstNameInput.dispatchEvent(event);
      firstNameInput.blur();

      setNativeValue(lastNameInput, lastName);
      lastNameInput.focus();
      lastNameInput.dispatchEvent(event);
      lastNameInput.blur();

      setNativeValue(emailInput, email);
      emailInput.focus();
      emailInput.dispatchEvent(event);
      emailInput.blur();
    });
  },
  // grab email
  getGuestEmail: () => window.data.email,
  // grab first name
  getGuestFirstName: () =>
    document
      .querySelector(Dedge.selectors.paymentPage.firstname)
      .textContent.trim(),
  // grab last name
  getGuestLastName: () => {
    let nameText = document.querySelector(Dedge.selectors.paymentPage.lastname)
      .textContent;
    var last = nameText.substring(
      nameText.lastIndexOf(" ") + 1,
      nameText.length
    );
    return last;
  },
  // grab rate type
  getRateType: () => {
    let rateNames: string[] = [];
    let rates = window.data.transaction.products;
    rates.forEach((item: any) => {
      if (item.type !== "HotelExtra") {
        rateNames.push(item.rate);
      }
    });

    return rateNames;
  },
  // grab roomName
  getRoomName: () => {
    let roomNames: string[] = [];
    let rooms = window.data.transaction.products;
    rooms.forEach((item: any) => {
      if (item.type !== "HotelExtra") {
        roomNames.push(item.name);
      }
    });

    return roomNames;
  },
  getNumberOfAdults: () => {
    return window.data.context.searchParameters.adultCount;
  },
  getNumberOfDays: () => {
    return window.data.context.searchParameters.nightCount;
  },
  // grab rateCodes
  getRateTypes: () => {
    let rateNames: string[] = [];
    let rates = window.data.transaction.products;
    rates.forEach((item: any) => {
      rateNames.push(item.rate);
    });

    return rateNames;
  },
  // no properties currently allow promo/group/rate codes
  // grab promo code applied
  getPromoCode: () => {
    return "";
  },
  // grab group code applied
  getGroupCode: () => {
    return "";
  },
  // grab subtotal to save
  getSubtotal: () => {
    let roomPrices = window.data.transaction.products;
    let total = 0;
    let extras = 0;
    roomPrices.forEach((item: any) => {
      if (item.type === "HotelExtra") {
        extras += item.price;
      }
      total += item.price - extras;
    });

    const taxedRate = window.gb_data.hotel.taxedRate;
    return Number((total / taxedRate).toFixed(2));
  },
  // grab grand total to save
  getTotalWithTaxes: () => {
    let roomPrices = window.data.transaction.products;
    let total = 0;
    let extras = 0;

    roomPrices.forEach((item: any) => {
      if (item.type === "HotelExtra") {
        extras += item.price;
      }
      total += item.price - extras;
    });
    return total;
  },
  // aggregrate reservation data
  getReservationData: () => {
    let resData: roomData[] = [];

    let rateTypes: string[] = [];
    let roomNames: string[] = [];

    let rooms = window.data.transaction.products;

    rooms.forEach((item: any, index: number) => {
      if (item.type !== "HotelExtra") {
        rateTypes.push(Dedge.getRateType()[index]);
        roomNames.push(Dedge.getRoomName()[index]);
      }
    });

    resData.push({
      email: Dedge.getGuestEmail(),
      first_name: Dedge.getGuestFirstName(),
      last_name: Dedge.getGuestLastName(),
      confirmation_number: Dedge.getConfirmationNumber()[0],
      checkin_date: Dedge.getPaymentPageCheckinDate(),
      checkout_date: Dedge.getPaymentPageCheckoutDate(),
      rate_type: rateTypes.join(" + "),
      room_name: roomNames.join(" + "),
      currency_code: Dedge.getCurrencyToSave(),
      lang_code: Dedge.getLanguage(),
      total: Dedge.getSubtotal().toString(),
      total_with_taxes: Dedge.getTotalWithTaxes().toString(),
      hotel_identifier: window.gb_data.widget.identifier,
      agreed_enrollment_checked: "false",
      ab_version_viewed: sessionStorage.getItem("gbShowEnrollment") as string,
      browser_info: getBrowserInfo(),
      is_mobile: getDeviceInfo(),
    });

    return resData;
  },
  // grabs cash rewards
  getRewards: (percentage: number) => {
    let calculatedRewards = 0;
    let total;
    let extras = 0;
    let extrasText = document.querySelectorAll(
      Dedge.selectors.paymentPage.extras
    );

    let totalText = document.querySelector(Dedge.selectors.ratesPage.total);

    if (totalText) {
      total = totalText.textContent.replace(/[^\d.-]/g, "");
    } else if (window.location.href.indexOf("bookingReference") > -1) {
      let roomPrices = window.data.transaction.products;
      let totals = 0;
      let extrasConfirmation = 0;
      roomPrices.forEach((item: any) => {
        if (item.type === "HotelExtra") {
          extrasConfirmation += item.price;
        }
        totals += item.price - extrasConfirmation;
      });
      total = totals;
    } else {
      total = 0;
    }

    if (extrasText.length > 0) {
      extrasText.forEach((item: any) => {
        extras += Number(item.textContent.replace(/[^\d.-]/g, ""));
      });
    }

    const taxedRate = window.gb_data.hotel.taxedRate;
    calculatedRewards = Number(
      (((total - extras) / taxedRate) * percentage).toFixed(2)
    );

    return calculatedRewards.toString();
  },
  // checks if messaging should be displayed
  isRateExcluded: (rate: string) => {
    const rateCodeExclusions = Dedge.hotel.rateCodeExclusions;
    for (const rateCodeExclusion of rateCodeExclusions as any) {
      if (rateCodeExclusion.rate_plan_code === rate) {
        return true;
      }
    }
    return false;
  },
  // gets the rate exclusion matching the rate code
  getRateExcluded: (rate: string) => {
    const packageRateExlusions = Dedge.hotel.packageRateExlusions;
    for (const packageRateExlusion of packageRateExlusions as any) {
      if (rate === packageRateExlusion.name) {
        return packageRateExlusion;
      }
    }
  },
  // checks if messaging should be displayed
  shouldDisplayMessaging: () => {
    //  if show enrollment (from AB is set to false)
    if (!JSON.parse(sessionStorage.getItem("gbShowEnrollment") || "true"))
      return false;
    return true;
  },
  // checks if messaging should be partially displayed
  shouldDisplayPartialMessaging: () => {
    return true;
  },
  /** Uses waitUntilExists to check if the user should see widget
   * @param handler
   */
  addWidgetListener: (handler) => {
    const timeout = waitForSelectors(Dedge.selectors.widget.waitFor, handler);
    if (Dedge.activeIntervals) {
      Dedge.activeIntervals.push(timeout);
    }
  },
  /** Uses waitUntilExists to check if the user is on rates page then executes the passed handler function
   * @param handler
   */
  addRatesPageListener: (handler) => {
    const timeout = waitForSelectors(
      Dedge.selectors.ratesPage.waitFor,
      handler
    );
    if (Dedge.activeIntervals) {
      Dedge.activeIntervals.push(timeout);
    }
  },
  /** Uses waitUntilExists to check if the user is on payment page then executes the passed handler function
   * @param handler
   */
  addPaymentPageListener: (handler) => {
    const timeout = waitForSelectors(
      Dedge.selectors.paymentPage.waitFor,
      handler
    );
    if (Dedge.activeIntervals) {
      Dedge.activeIntervals.push(timeout);
    }
  },
  /** Uses waitUntilExists to check if the user is on confirmation page then executes the passed handler function
   * @param handler
   */
  addConfirmationPageListener: (handler) => {
    const timeout = waitForSelectors(
      Dedge.selectors.confirmationPage.waitFor,
      handler
    );
    if (Dedge.activeIntervals) {
      Dedge.activeIntervals.push(timeout);
    }
  },
  // listen for cancellation number
  addCancellationPageListener: () => {
    if (Dedge.addCancellationhandler) {
      const timeout = waitForSelectors(
        Dedge.selectors.confirmationPage.cancellationTarget,
        Dedge.addCancellationhandler
      );
      if (Dedge.activeIntervals) {
        Dedge.activeIntervals.push(timeout);
      }
    }
  },
  // submit cancellation
  addCancellationhandler: () => {
    const cancellationNumber = document.querySelector(
      Dedge.selectors.confirmationPage.cancellationNumber
    ).textContent;

    const today = moment().format("YYYY-MM-DD");

    if (cancellationNumber && cancellationNumber.length > 0) {
      const cancellationData = {
        confirmation_number: cancellationNumber,
        cancellation_number: cancellationNumber,
        cancellation_date: today,
        hotel_identifier: window.gb_data.widget.identifier,
      };
      axios
        .post(
          `${
            guestbookSites[environment as string].rewardsdash.url
          }/api/v1/submit_booking_engine_cancellation`,
          cancellationData,
          {
            headers: {
              "Content-Type": "application/json",
              "Access-Control-Allow-Origin": "*",
            },
          }
        )
        .then((response) => {
          if (response.data.success) {
            const element = document.querySelector(
              Dedge.selectors.confirmationPage.confirmationMessageContainer
            );
            if (!!element) {
              element.style.display = "none";
            }
          } else {
            console.log(response);
          }
        })
        .catch((error) => {
          console.log(error);
        });
    }
  },
  // create widget container
  createWidgetContainer: () => {
    document.querySelector(Dedge.selectors.widget.widget)?.remove();
    if (!document.querySelector(Dedge.selectors.widget.widget)) {
      const messageTarget = document.querySelector(
        Dedge.selectors.widget.target
      );
      if (messageTarget) {
        const injectedElement = document.createElement("div");
        injectedElement.id = Dedge.selectors.widget.widget.substr(1);
        messageTarget.appendChild(injectedElement);
      }
    }
  },
  // create mobile widget container
  createWidgetMobileContainer: () => {
    document.querySelector(Dedge.selectors.widget.widget)?.remove();
    if (!document.querySelector(Dedge.selectors.widget.widget)) {
      const messageTarget = document.querySelector(
        Dedge.selectors.widget.mobileWidgetTarget
      );
      const messageTargetHeight = document.querySelector(
        Dedge.selectors.widget.mobileHeightTarget
      ).offsetHeight;
      if (messageTarget) {
        const injectedElement = document.createElement("div");
        injectedElement.id = Dedge.selectors.widget.widget.substr(1);
        injectedElement.setAttribute(
          "style",
          `
          left: 0px;
          width: 100%;
          position: fixed;
          bottom: ${messageTargetHeight}px;
          z-index: 1;
           `
        );
        messageTarget.after(injectedElement);
      }
    }
  },
  additionalWidgetFunctions: () => {
    document.head.insertAdjacentHTML(
      "beforeend",
      `<style id="gb-override-styles" type="text/css">
         .MuiAccordionSummary-content div:first-child div:nth-child(2),
         .MuiAccordionSummary-content div:first-child div:nth-child(2) div:first-child{
            color: white;
         }
        </style>`
    );
  },
  // create payment message container
  createEnrollmentMessageContainer: () => {
    if (document.querySelector(Dedge.selectors.paymentPage.email)) {
      if (
        !document.querySelector(
          Dedge.selectors.paymentPage.enrollmentMessageContainer
        )
      ) {
        const messageTarget = document.querySelector(
          Dedge.selectors.paymentPage.email
        ).parentElement.parentElement;

        if (messageTarget) {
          const injectedElement = document.createElement("div");
          injectedElement.id = Dedge.selectors.paymentPage.enrollmentMessageContainer.substr(
            1
          );
          messageTarget.after(injectedElement);
        }
      }

      waitForSelectors(Dedge.selectors.paymentPage.checkbox, () => {
        let checkbox = document.querySelector(
          Dedge.selectors.paymentPage.checkbox
        );
        let paymentPageReserveButton = document.querySelector(
          Dedge.selectors.paymentPage.paymentPageReserveButton
        );
        paymentPageReserveButton.addEventListener("click", () => {
          if (checkbox.checked) {
            const submissionData = {
              account: {
                email: document
                  .querySelector(Dedge.selectors.paymentPage.email)
                  .value.trim(),
                first_name: document
                  .querySelector(Dedge.selectors.paymentPage.firstNameInput)
                  .value.trim(),
                last_name: document
                  .querySelector(Dedge.selectors.paymentPage.lastNameInput)
                  .value.trim(),
                wid: window.gb_data.widget.identifier,
              },
            };
            axios
              .post(
                `${
                  guestbookSites[environment as string].rewardsdash.url
                }/api/v1/accounts`,
                submissionData,
                {
                  headers: {
                    "Content-Type": "application/json",
                    "Access-Control-Allow-Origin": "*",
                  },
                }
              )
              .then((response) => {
                if (response.status === 201) {
                  localStorage.setItem(
                    "gb_storage_key",
                    response.data.user_token
                  );
                } else {
                  console.log(response);
                }
              })
              .catch((error) => {
                console.log(error.response.data.error);
              });
          }
        });
      });
    }
  },
  // create confirmation message container
  createConfirmationMessageContainer: () => {
    if (
      !document.querySelector(
        Dedge.selectors.confirmationPage.confirmationMessageContainer
      )
    ) {
      const messageTarget = document.querySelector(
        Dedge.selectors.confirmationPage.messageTarget
      );
      if (messageTarget) {
        const injectedElement = document.createElement("div");
        injectedElement.id = Dedge.selectors.confirmationPage.confirmationMessageContainer.substr(
          1
        );
        messageTarget.insertAdjacentElement("afterend", injectedElement);
      }
    }
  },
  // set locale
  setLocale: () => {
    window.gb_data.I18n.locale = Dedge.getLanguage();
  },
  // grab subtotal to use to calculate rewards and display on enrollment/widget
  displaySubtotal: () => {
    let total = document
      .querySelector(Dedge.selectors.ratesPage.total)
      .textContent.split(" ")[1];
    return total;
  },
  // save reservation data on the payment page
  savePaymentPageInfo: () => {
    const saveReservationData = (): void => {
      const resData = Dedge.getReservationData();
      const email = resData[0].email;
      axios
        .get(
          `${
            guestbookSites[environment as string].rewardsdash.url
          }/api/v1/bei/member_info`,
          {
            params: {
              email: email,
            },
          }
        )
        .then((response) => {
          if (response.status === 200) {
            if (response.data.is_current_member) {
              sessionStorage.setItem("isMemberOrEnrolled", "true");
            } else {
              sessionStorage.setItem("isMemberOrEnrolled", "false");
            }
          } else {
            sessionStorage.setItem("isMemberOrEnrolled", "false");
          }
        })
        .catch((error) => {
          console.log(error);
          sessionStorage.setItem("isMemberOrEnrolled", "false");
        });
      sessionStorage.setItem("gbBookingDetails", JSON.stringify(resData));
    };

    let confirmationPage = document.querySelector(
      Dedge.selectors.confirmationPage.waitFor
    );
    if (confirmationPage) {
      saveReservationData();
    }
  },
  clearAllListeners: () => {
    if (!Dedge.activeIntervals) return;
    let length = Dedge.activeIntervals.length;
    for (let i = 0; i < length; i++) {
      let int = Dedge.activeIntervals.pop();
      clearInterval(int);
    }
  },
  injectIntegration: () => {
    handleIntegrationInjector(Dedge);
  },
  //  We need to change the overflow of the rates page so that the hoverover shows correctly
  additionalRatesPageFunctions: () => {
    document.head.insertAdjacentHTML(
      "beforeend",
      `<style id="gb-override-styles" type="text/css">
          .gb-rates-message {
            font-size: 13px;
            margin-bottom: 4px;
          }
        </style>`
    );
  },

  additionalPaymentsPageFunctions: () => {
    document.head.insertAdjacentHTML(
      "beforeend",
      `<style id="gb-override-styles" type="text/css">
         #guestbook-enrollment {
            width: 100%;
            padding: 0 0 10px 0;
         }
         #guestbook-enrollment div:first-child div:first-child div:nth-child(2),
         #guestbook-enrollment div:first-child div:first-child div:first-child,
         #guestbook-enrollment div:first-child div:first-child div:first-child span,
         #guestbook-enrollment div:first-child div:nth-child(2) div:first-child span{
            color: white;
         }
       </style>`
    );
  },

  additionalConfirmationPageFunctions: () => {
    var script = document.createElement("script");
    script.innerText =
      "$(document).on('bookingEngine:transactionCompleted', function (eventData, transactionCompletedParams) {window.data = transactionCompletedParams;});bookingEngine.track.transactionCompleted()";

    document.body.append(script);
    document.head.insertAdjacentHTML(
      "beforeend",
      `<style id="gb-override-styles-confirmation-page" type="text/css">
         #guestbook-widget-desktop, #guestbook-widget {
           display: none;
         }
       </style>`
    );
  },
};

export default Dedge;
